import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer,
  ViewChild,
} from '@angular/core';
import { CropperService } from './cropper.service';
import { T2LoadingService } from '../../../trip2-theme/components/t2-loading/t2-loading.service';
import { DomSanitizer } from '@angular/platform-browser';
import { Subscription } from 'rxjs/Subscription';
import { SweetAlertHelper } from '../../../core/helpers/sweet-alert-helper.service';
import { ReservationService } from '../../trip-reservations/reservations.service';

declare const Cropper: any;
declare const pdfjsLib: any;

@Component({
  selector: 'app-cropper',
  templateUrl: './cropper.component.html',
  styleUrls: ['./cropper.component.scss']
})
export class CropperComponent implements OnInit,  AfterViewInit, OnDestroy {

  protected _cropperImg: any;
  protected cutout = undefined;
  protected initCrop = false;
  @Input() imageUrl: any;
  @Input() tripId: any;
  @Output() notify: EventEmitter < any > = new EventEmitter < any > ();
  @ViewChild('img') protected img: ElementRef;
  @ViewChild('fileUploadImage') protected _fileUpload: ElementRef;
  @ViewChild('canvas') canvas: ElementRef;
  private observables: Subscription[] = [];
  ctx: CanvasRenderingContext2D;
  protected configImg = {
    aspectRatio: NaN,
    checkCrossOrigin: true,
    background: true,
    checkOrientation: true,
    scalable: true,
    rotatable: true,
    viewMode: 0
  };
  private allPages = [];
  readOnlyState: boolean;

  constructor(
    private sanitizer: DomSanitizer,
    private _service: CropperService,
    private _loading: T2LoadingService,
    private sweetAlertHelper: SweetAlertHelper,
    private editReserationService: ReservationService,
  ) { }

  ngOnInit() {
    this.observables.push(
      this.editReserationService.isReadOnly$.subscribe({
        next: (isReadOnly) => {
          this.readOnlyState = isReadOnly;
        },
      }),
    );
  }

  ngAfterViewInit(): void {
    this.ctx = (<HTMLCanvasElement>this.canvas.nativeElement).getContext('2d');
  }

  public fileClick() {
    this._fileUpload.nativeElement.click();
  }

  initCropper()  {
    this.destroyCropper();

    this.img.nativeElement.src = this.imageUrl;
    this._cropperImg = new Cropper(this.img.nativeElement, this.configImg);
    this.initCrop = true;
  }

  clearCropper() {
    this._cropperImg.clear();
    this._cropperImg.setDragMode('move');
    this.initCrop = false;
  }

  crop() {
    if (!this.isCropperReady()) {
      return;
    }
    this.cutout = this._cropperImg.getCroppedCanvas({
      width: 1000,
      height: 1000
    }).toDataURL('image/png', 60 / 100);
    this.initCrop = false;
  }

  save() {
    if (this.cutout && this.tripId) {
      this._loading.show('The signature is being sent')
      const img = this.cutout.split('base64,')[1];
      const data = {
        TripId: this.tripId,
        SignatureRider: img
      };
      this.observables.push(
      this._service
        .updateSignature(data)
        .finally( () => this.cutout = undefined)
        .subscribe(
          resp => {
            this.updateImg(resp.data.signaturephoto);
            this.notify.emit(resp);
            this._loading.hiden();
          },
          error => {this._loading.hiden(); }
        ));
    }
  }

  updateImg(urlImg) {
    this.destroyCropper();
    this.imageUrl = urlImg;
    this.img.nativeElement.src = urlImg;
    this.initCrop = false;
  }

  fileUp(event) {
    this.allPages = [];
    const files = event.srcElement.files;
    if (files.length && files) {
      const ext = files[0].name.split('.').pop();
      const file = URL.createObjectURL(files[0]);
      const img = ['jpg', 'jpeg', 'png', 'JPG', 'JPEG', 'PNG'];
      const doc = ['pdf', 'PDF'];

      if (img.indexOf(ext) > -1) {
        /**********************************************************************/
        this.destroyCropper();
        this.imageUrl = file;
        this.img.nativeElement.src = file;
        this._cropperImg = new Cropper(this.img.nativeElement, this.configImg);
        this.initCrop = false;
        /**********************************************************************/
      } else if (doc.indexOf(ext) > -1) {
          this.convertPdfToImage(file);
      } else {
        this.sweetAlertHelper.createCustomAlert({
          position: 'top-end',
          type: 'error',
          title: 'Format not allowed, use jpg, jpeg, png or pdf formats',
          showConfirmButton: false,
          timer: 3000,
        });
      }
    }
  }

  destroyCropper() {
    if (this._cropperImg) {
      this._cropperImg.destroy();
    }
  }

  convertPdfToImage(file) {
    this._loading.show('Loading the PDF')
    pdfjsLib.getDocument(file).promise.then((pdfDoc) => {
      const totalPages = pdfDoc.numPages;

      for (let i = 1; i <= totalPages; i++) {
        pdfDoc.getPage(i).then((page) => {
          const width = window.screen.availWidth - 1050;
          const viewportInitial = page.getViewport({ scale: 1 });
          if (!viewportInitial || !viewportInitial.width) {
            return;
          }
          const scale_required = width / viewportInitial.width;
          const viewport = page.getViewport({ scale: scale_required });
          if (!viewport || !viewport.height) {
            return;
          }

          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            return;
          }

          canvas.width = width;
          canvas.height = viewport.height;

          const renderContext = {
            canvasContext: ctx,
            viewport: viewport
          };

          const renderTask = page.render(renderContext);
          renderTask.promise.then(() => {
            this.allPages.push(canvas.toDataURL());
            this._loading.hiden();
          });
        });
      }
    });
  }

  zoomIn() {
    if (!this.isCropperReady()) {
      return;
    }
    this._cropperImg.zoom(0.1);
  }

  zoomOut() {
    if (!this.isCropperReady()) {
      return;
    }
    this._cropperImg.zoom(-0.1);
  }

  private isCropperReady(): boolean {
    if (!this._cropperImg) {
      return false;
    }
    const imageData = this._cropperImg.getImageData();
    return !!(imageData && imageData.naturalWidth && imageData.naturalHeight);
  }

  selectedImg($i: any) {
    const pages = this.allPages;
    this.updateImg(pages[$i]);
    this.allPages = [];
  }

  sanitize(url: string) {
    if (url) {
      return this.sanitizer.bypassSecurityTrustUrl(url);
    }
  }

  ngOnDestroy(): void {
    this.observables.forEach(item => item.unsubscribe());
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/A2-utils/cropper/cropper.component.ts